'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
let counter = 0;
function newClientMessageId() {
    counter += 1;
    const rand = Math.random().toString(36).slice(2, 10);
    return `c_${Date.now().toString(36)}${counter.toString(36)}${rand}`;
}
function merge(entries, incoming) {
    const byId = new Map(entries.map((e) => [e.id, e]));
    for (const m of incoming)
        byId.set(m.id, { ...m });
    return [...byId.values()];
}
const INITIAL = { phase: 'idle', entries: [], typing: null };
/** Maximum length of a message (the Support Chat API limit). */
export const SUPPORT_MESSAGE_MAX = 5000;
/**
 * Drives the panel's chat from a SupportChatClient: connects on demand with a
 * timeout, keeps messages across close and reopen, sends with optimistic
 * entries and retry, and follows live events. Exported for products that
 * build their own chat surface on the same client.
 */
export function useSupportChat(client, { timeout = 15_000 } = {}) {
    const [state, setState] = useState(INITIAL);
    const stateRef = useRef(state);
    stateRef.current = state;
    const clientRef = useRef(client);
    clientRef.current = client;
    const abort = useRef(null);
    const alive = useRef(true);
    const lastRead = useRef(null);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            abort.current?.abort();
        };
    }, []);
    const connect = useCallback(() => {
        const c = clientRef.current;
        if (!c)
            return;
        abort.current?.abort();
        const ctl = new AbortController();
        abort.current = ctl;
        setState((s) => ({ ...s, phase: 'connecting', reason: undefined }));
        const current = () => alive.current && abort.current === ctl;
        let timedOut = false;
        const timer = timeout > 0
            ? setTimeout(() => {
                timedOut = true;
                ctl.abort();
                if (current())
                    setState((s) => ({ ...s, phase: 'error', reason: 'timeout' }));
            }, timeout)
            : undefined;
        c.connect({ signal: ctl.signal }).then((snap) => {
            clearTimeout(timer);
            if (!current() || timedOut)
                return;
            setState((s) => ({
                ...s,
                phase: 'ready',
                entries: merge(s.entries.filter((e) => e.pending), snap.messages),
                availability: snap.availability ?? s.availability,
            }));
        }, () => {
            clearTimeout(timer);
            if (!current() || timedOut)
                return;
            setState((s) => ({ ...s, phase: 'error', reason: 'failed' }));
        });
    }, [timeout]);
    // Live events, for as long as the panel lives and has connected once.
    const subscribed = state.phase !== 'idle';
    useEffect(() => {
        if (!subscribed || !client?.subscribe)
            return;
        return client.subscribe((event) => {
            if (!alive.current)
                return;
            setState((s) => {
                switch (event.type) {
                    case 'message': {
                        // The stored copy of a message we sent replaces its optimistic entry.
                        return { ...s, entries: merge(s.entries, [event.message]), typing: null };
                    }
                    case 'typing':
                        return { ...s, typing: event.typing ? { name: event.name } : null };
                    case 'availability':
                        return { ...s, availability: event.availability };
                    case 'ended':
                        return { ...s, phase: 'ended', reason: event.reason, typing: null };
                    default:
                        return s;
                }
            });
        });
    }, [subscribed, client]);
    const deliver = useCallback((clientMessageId, text) => {
        const c = clientRef.current;
        if (!c)
            return;
        c.send(text, { clientMessageId }).then((stored) => {
            if (!alive.current)
                return;
            setState((s) => ({
                ...s,
                entries: merge(s.entries.filter((e) => e.clientMessageId !== clientMessageId), [stored]),
            }));
        }, () => {
            if (!alive.current)
                return;
            setState((s) => ({
                ...s,
                entries: s.entries.map((e) => e.clientMessageId === clientMessageId ? { ...e, pending: 'failed' } : e),
            }));
        });
    }, []);
    const send = useCallback((raw) => {
        const text = raw.trim().slice(0, SUPPORT_MESSAGE_MAX);
        if (!text)
            return;
        const clientMessageId = newClientMessageId();
        setState((s) => ({
            ...s,
            entries: [
                ...s.entries,
                {
                    id: clientMessageId,
                    clientMessageId,
                    author: 'visitor',
                    text,
                    createdAt: new Date(),
                    pending: 'sending',
                },
            ],
        }));
        deliver(clientMessageId, text);
    }, [deliver]);
    const resend = useCallback((clientMessageId) => {
        const entry = stateRef.current.entries.find((e) => e.clientMessageId === clientMessageId);
        if (!entry || entry.pending !== 'failed')
            return;
        setState((s) => ({
            ...s,
            entries: s.entries.map((e) => e.clientMessageId === clientMessageId ? { ...e, pending: 'sending' } : e),
        }));
        deliver(clientMessageId, entry.text);
    }, [deliver]);
    const markRead = useCallback(() => {
        const last = [...state.entries].reverse().find((e) => !e.pending && e.author !== 'visitor');
        if (!last || last.id === lastRead.current)
            return;
        lastRead.current = last.id;
        clientRef.current?.markRead?.(last.id);
    }, [state.entries]);
    return { state, connect, send, resend, markRead };
}
//# sourceMappingURL=support-panel.client.js.map